// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Col, Row } from 'antd';
import DataGrid from '@dtci/spellbook/DataGrid';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';
// --- Hooks & Store
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useJobTaskActions } from 'datastage/store/jobTasks/useJobTasks';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Grid Utils
import { useJobTasksGridUtils } from './utils/gridUtils';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Job Tasks Grid
 *
 * Features:
 * - Displays a data grid of job tasks with filtering options
 * - Fetches task data from API based on filters and sorting
 *
 * @returns {Element}
 * @constructor
 */
export const JobTasksGrid = () => {
    const agGridApi = useAgGridApi();
    const {
        isModalOpen,
        filterForm,
        rangeParam,
        sortParam,
        tasks,
        defaultFilterRange,
    } = useSelector((state) => state.jobTasks);
    const { jobStatusOptions } = useSelector((s) => s.jobs);
    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    const { updateJobTasks, updateJobTasksForm } = useJobTaskActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();

    // Grid utilities for sorting and columns
    const { setSort, columns } = useJobTasksGridUtils({
        datastageApi,
        filterForm,
        defaultFilterRange,
        updateJobTasks,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: []
        }
    });

    const fetchJobTasks = async () => {
        try {

            setLoadingTrue();
            const data = await datastageApi.jobTasks(
                filterCriteria(filterForm, '', 'jobTasks'),
                rangeParam,
                sortParam,
            );

            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch job tasks data.');
                return;
            }
            updateJobTasks({
                tasks: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber
            });
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fetch job tasks data.');
            console.error('Error fetching job tasks:', error);
        } finally {
            setLoadingFalse();
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            fetchJobTasks();
        }
    }, [isModalOpen]);

    const handleInputChange = (field, value) => {
        updateJobTasksForm({
            key: 'filterForm',
            data: { [field]: value },
        });
    };

    return (
        <>
            <Row>
                <Col >
                    <InputLabel colProps={{ span: 24 }} label="Job ID">
                        <Input
                            value={filterForm.jobId}
                            onChange={e => handleInputChange('jobId', e.target.value)}
                            width="medium"
                            id="jobId"
                            name="jobId"
                            placeholder="Enter"
                        />
                    </InputLabel>
                </Col>
                <Col >
                    <InputLabel colProps={{ span: 24 }} label="Task Status">
                        <Select
                            options={jobStatusOptions}
                            isClearable
                            isSearchable
                            onChange={value => {
                                handleInputChange('taskStatus', value);
                                fetchJobTasks();
                            }}
                            value={filterForm.taskStatus}
                        />
                    </InputLabel>
                </Col>
            </Row>
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: tasks,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Job Tasks Found'}
            />
        </>
    );
};
